import React, { useState, useEffect } from 'react';
import { TrendingUp, Loader2, AlertCircle, Users, Eye, BarChart2 } from 'lucide-react';
import { supabase } from '@/utils/supabase';

function fmtCurrency(cents) {
  return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 });
}

export default function AdminAnalytics() {
  const [data, setData]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  // Engagement
  const [views, setViews]         = useState([]);
  const [viewsLoading, setViewsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: orders, error: qErr } = await supabase
          .from('orders')
          .select('amount_total, created_at, customer_email')
          .eq('status', 'paid')
          .order('created_at', { ascending: true });
        if (qErr) throw qErr;

        const now    = Date.now();
        const cutoff = now - 30 * 86400000;
        const dayMap = new Map();

        for (const o of (orders || [])) {
          if (!o.created_at) continue;
          const ts = new Date(o.created_at).getTime();
          if (ts < cutoff) continue;
          const day = new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          if (!dayMap.has(day)) dayMap.set(day, { day, revenue: 0, orders: 0 });
          const d = dayMap.get(day);
          d.revenue += o.amount_total || 0;
          d.orders++;
        }

        setData([...dayMap.values()]);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();

    // Fetch page views (last 30 days)
    (async () => {
      try {
        const cutoffISO = new Date(Date.now() - 30 * 86400000).toISOString();
        const { data: pv, error: pvErr } = await supabase
          .from('page_views')
          .select('session_id, page, created_at')
          .gte('created_at', cutoffISO)
          .order('created_at', { ascending: true });
        if (pvErr) throw pvErr;
        setViews(pv || []);
      } catch {
        // silently ignore – table may not exist yet
      } finally {
        setViewsLoading(false);
      }
    })();
  }, []);

  const totalRevenue = data.reduce((s, d) => s + d.revenue, 0);
  const totalOrders  = data.reduce((s, d) => s + d.orders, 0);
  const maxRevenue   = Math.max(...data.map((d) => d.revenue), 1);

  // Engagement derived stats
  const totalPageViews   = views.length;
  const uniqueVisitors   = new Set(views.map((v) => v.session_id)).size;
  const viewsByDay = (() => {
    const m = new Map();
    for (const v of views) {
      const day = new Date(v.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      m.set(day, (m.get(day) || 0) + 1);
    }
    return [...m.entries()].map(([day, count]) => ({ day, count }));
  })();
  const topPages = (() => {
    const m = new Map();
    for (const v of views) {
      const pg = v.page || '/';
      m.set(pg, (m.get(pg) || 0) + 1);
    }
    return [...m.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([page, count]) => ({ page, count }));
  })();
  const maxViewCount = Math.max(...viewsByDay.map((d) => d.count), 1);

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl text-[var(--ink)]">Analytics</h1>
        <p className="text-[var(--muted)] text-xs font-['Space_Mono'] mt-0.5">Last 30 days</p>
      </div>

      {/* ── Website Engagement ─────────────────────────────── */}
      <div>
        <h2 className="font-['Space_Mono'] text-[0.62rem] tracking-[0.16em] uppercase text-[var(--muted)] mb-3">Website Engagement</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[var(--paper)] border border-[var(--line)] p-5 flex items-start gap-3">
            <Eye size={14} className="text-[var(--brand)] mt-1 shrink-0" />
            <div>
              <p className="font-['Space_Mono'] text-[0.6rem] tracking-[0.16em] uppercase text-[var(--muted)] mb-1">Page Views</p>
              {viewsLoading
                ? <Loader2 size={14} className="animate-spin text-[var(--brand)]" />
                : <p className="text-2xl font-['Cormorant_Garamond'] text-[var(--ink)]">{totalPageViews.toLocaleString()}</p>}
            </div>
          </div>
          <div className="bg-[var(--paper)] border border-[var(--line)] p-5 flex items-start gap-3">
            <Users size={14} className="text-[var(--brand)] mt-1 shrink-0" />
            <div>
              <p className="font-['Space_Mono'] text-[0.6rem] tracking-[0.16em] uppercase text-[var(--muted)] mb-1">Unique Visitors</p>
              {viewsLoading
                ? <Loader2 size={14} className="animate-spin text-[var(--brand)]" />
                : <p className="text-2xl font-['Cormorant_Garamond'] text-[var(--ink)]">{uniqueVisitors.toLocaleString()}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Daily Page Views chart */}
      <div className="bg-[var(--paper)] border border-[var(--line)] p-5">
        <div className="flex items-center gap-2 mb-5">
          <BarChart2 size={12} className="text-[var(--muted)]" />
          <h2 className="font-['Space_Mono'] text-[0.62rem] tracking-[0.16em] uppercase text-[var(--muted)]">Daily Page Views</h2>
        </div>
        {viewsLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 size={16} className="animate-spin text-[var(--brand)]" />
          </div>
        ) : viewsByDay.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 gap-2">
            <Eye size={20} className="text-[var(--muted)]" strokeWidth={1} />
            <p className="text-[var(--muted)] text-xs font-['Space_Mono']">No visits recorded yet</p>
          </div>
        ) : (
          <div className="flex items-end gap-1 h-32 overflow-x-auto">
            {viewsByDay.map((d) => (
              <div key={d.day} className="flex flex-col items-center gap-1 flex-1 min-w-[24px]">
                <div
                  className="w-full bg-[var(--brand)]/30 hover:bg-[var(--brand)]/60 transition-colors cursor-default"
                  style={{ height: `${Math.round((d.count / maxViewCount) * 100)}px`, minHeight: '2px' }}
                  title={`${d.day}: ${d.count} views`}
                />
                <span className="text-[0.5rem] font-['Space_Mono'] text-[var(--muted)] rotate-45 origin-left whitespace-nowrap hidden sm:block">
                  {d.day}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top Pages */}
      <div className="bg-[var(--paper)] border border-[var(--line)] overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--line)]">
          <h2 className="font-['Space_Mono'] text-[0.62rem] tracking-[0.16em] uppercase text-[var(--muted)]">Top Pages</h2>
        </div>
        {viewsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={16} className="animate-spin text-[var(--brand)]" />
          </div>
        ) : topPages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2">
            <p className="text-[var(--muted)] text-xs font-['Space_Mono']">No page data yet</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--line)]">
            {topPages.map(({ page, count }) => (
              <div key={page} className="px-5 py-3 flex items-center gap-3 hover:bg-white/3 transition-colors">
                <span className="flex-1 text-xs text-[var(--ink)] font-['Space_Mono'] truncate">{page}</span>
                <div className="flex items-center gap-3 shrink-0">
                  <div
                    className="h-1 bg-[var(--brand)]/40 rounded"
                    style={{ width: `${Math.round((count / topPages[0].count) * 80)}px` }}
                  />
                  <span className="text-xs font-['Space_Mono'] text-[var(--muted)] w-10 text-right">{count}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Revenue ────────────────────────────────────────── */}
      <div>
        <h2 className="font-['Space_Mono'] text-[0.62rem] tracking-[0.16em] uppercase text-[var(--muted)] mb-3">Revenue</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[var(--paper)] border border-[var(--line)] p-5">
            <p className="font-['Space_Mono'] text-[0.6rem] tracking-[0.16em] uppercase text-[var(--muted)] mb-2">Revenue</p>
            <p className="text-2xl font-['Cormorant_Garamond'] text-[var(--ink)]">{fmtCurrency(totalRevenue)}</p>
          </div>
          <div className="bg-[var(--paper)] border border-[var(--line)] p-5">
            <p className="font-['Space_Mono'] text-[0.6rem] tracking-[0.16em] uppercase text-[var(--muted)] mb-2">Orders</p>
            <p className="text-2xl font-['Cormorant_Garamond'] text-[var(--ink)]">{totalOrders}</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-400 text-xs font-['Space_Mono'] p-3 bg-red-400/10 border border-red-400/20">
          <AlertCircle size={12} /><span>{error}</span>
        </div>
      )}

      {/* Daily Revenue chart */}
      <div className="bg-[var(--paper)] border border-[var(--line)] p-5">
        <h2 className="font-['Space_Mono'] text-[0.62rem] tracking-[0.16em] uppercase text-[var(--muted)] mb-5">
          Daily Revenue
        </h2>
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 size={18} className="animate-spin text-[var(--brand)]" />
          </div>
        ) : data.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 gap-3">
            <TrendingUp size={24} className="text-[var(--muted)]" strokeWidth={1} />
            <p className="text-[var(--muted)] text-sm font-['Space_Grotesk']">No data for the last 30 days</p>
          </div>
        ) : (
          <div className="flex items-end gap-1 h-40 overflow-x-auto">
            {data.map((d) => (
              <div key={d.day} className="flex flex-col items-center gap-1 flex-1 min-w-[24px] group">
                <div className="relative w-full group">
                  <div
                    className="w-full bg-[var(--brand)]/40 hover:bg-[var(--brand)] transition-colors cursor-default"
                    style={{ height: `${Math.round((d.revenue / maxRevenue) * 120)}px`, minHeight: '2px' }}
                    title={`${d.day}: ${fmtCurrency(d.revenue)}`}
                  />
                </div>
                <span className="text-[0.5rem] font-['Space_Mono'] text-[var(--muted)] rotate-45 origin-left whitespace-nowrap hidden sm:block">
                  {d.day}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Revenue table */}
      {!loading && data.length > 0 && (
        <div className="bg-[var(--paper)] border border-[var(--line)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[var(--line)]">
                  {['Date', 'Orders', 'Revenue'].map((h) => (
                    <th key={h} className="text-left px-5 py-3 font-['Space_Mono'] text-[0.58rem] tracking-[0.12em] uppercase text-[var(--muted)]">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--line)]">
                {[...data].reverse().map((d) => (
                  <tr key={d.day} className="hover:bg-white/3 transition-colors">
                    <td className="px-5 py-3 text-[var(--ink)]">{d.day}</td>
                    <td className="px-5 py-3 font-['Space_Mono'] text-[var(--muted)]">{d.orders}</td>
                    <td className="px-5 py-3 font-['Space_Mono'] text-[var(--brand)]">{fmtCurrency(d.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
